import { useState, useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";

const CHANGE_EVENT = "ws-localstorage-change";

/**
 * Like useState, but persists per-workspace to localStorage. Reloads on workspace switch.
 *
 * Storage key: `ws_${workspaceId}__${suffix}`. When workspaceId is null (pre-auth or
 * between workspace selection), behaves as in-memory state — no localStorage I/O.
 *
 * Cross-instance sync: writes dispatch a `ws-localstorage-change` event so other
 * hook instances sharing the same key stay in sync within the same tab (the native
 * `storage` event only fires cross-tab). This matters when two components each
 * mount their own copy of a shared hook (e.g. useListFolders).
 */
/** Full localStorage key for a workspace-scoped entry. Exported so non-hook code
 * (e.g. the idle prefetch) can read the same entries this hook writes. */
export function workspaceStorageKey(workspaceId: string, suffix: string): string {
  return `ws_${workspaceId}__${suffix}`;
}

export function useWorkspaceLocalStorage<T>(
  suffix: string,
  defaultValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  const workspaceId = useWorkspaceId();
  const key = workspaceId ? workspaceStorageKey(workspaceId, suffix) : null;

  const defaultRef = useRef(defaultValue);
  defaultRef.current = defaultValue;

  const [value, setValue] = useState<T>(() => readKey(key, defaultRef.current));

  const lastKey = useRef(key);
  useEffect(() => {
    if (lastKey.current === key) return;
    lastKey.current = key;
    setValue(readKey(key, defaultRef.current));
  }, [key]);

  // Track the last serialized value we wrote from this instance so we can skip
 // re-parsing our own broadcasts.
  const lastWrittenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!key) return;
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
      lastWrittenRef.current = serialized;
      // Notify other in-tab instances bound to this key.
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { key, value: serialized } }));
    } catch {
      // Ignore errors (e.g., quota exceeded, private browsing)
    }
  }, [key, value]);

  // Subscribe to in-tab and cross-tab writes so this instance picks up changes
  // made by sibling instances (e.g. a dialog writing a scope that a dropdown
  // needs to display).
  useEffect(() => {
    if (!key) return;
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<{ key: string; value: string }>).detail;
      if (!detail || detail.key !== key) return;
      if (detail.value === lastWrittenRef.current) return;
      try {
        setValue(JSON.parse(detail.value));
        lastWrittenRef.current = detail.value;
      } catch {
        // ignore
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key || e.newValue === null) return;
      if (e.newValue === lastWrittenRef.current) return;
      try {
        setValue(JSON.parse(e.newValue));
        lastWrittenRef.current = e.newValue;
      } catch {
        // ignore
      }
    };
    window.addEventListener(CHANGE_EVENT, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, [key]);

  return [value, setValue];
}

function readKey<T>(key: string | null, defaultValue: T): T {
  if (!key) return defaultValue;
  try {
    const raw = localStorage.getItem(key);
    if (raw !== null) return JSON.parse(raw);
  } catch {
    // Ignore errors (e.g., invalid JSON)
  }
  return defaultValue;
}
